import useApi from "@/hooks/useApi";
import { calculateMonthlyPrice, formatDuration } from "@/utilities/functions";
import PackageRepository from "@/utilities/repositories/Package";
import { IPackage } from "@/utilities/types/Package";
import cn from "@/utils/class-names";
import { packageCodeSchema } from "@/utils/validators/package-code.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Input, Text, Title } from "rizzui";

interface PlanProps {
  name: string;
  price: number;
  monthlyPrice: number;
  description: string;
  features?: string[];
  isPopular?: boolean;
  onGetStarted?: () => void;
  duration?: number;
}

type FormValues = {
  code: string;
};

export const PricingPlan: React.FC<PlanProps> = ({
  name,
  price,
  monthlyPrice,
  description,
  isPopular,
  onGetStarted,
  duration,
}) => {
  return (
    <div className={cn("relative")}>
      <div
        className={cn(
          "rounded-2xl flex flex-col gap-3 h-full p-8 transition-all duration-200",
          isPopular
            ? "bg-blue-600 shadow-[0_8px_28px_rgba(59,130,246,0.25)]"
            : "bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_28px_rgba(0,0,0,0.12)]"
        )}
      >
        {isPopular && (
          <Title as="h5" className={cn("text-lg", isPopular && "text-white")}>
            Most Popular!
          </Title>
        )}
        <Title as="h3" className={cn("text-2xl", isPopular && "text-white")}>
          {name}
        </Title>
        <div className="mt-4">
          <Text className={cn("text-2xl font-bold", isPopular && "text-white")}>
            ${Number(price).toFixed(0)}
          </Text>

          {duration && (
            <Text
              className={cn(
                "mt-2",
                isPopular ? "text-gray-100" : "text-gray-500"
              )}
            >
              for {formatDuration(duration)}
            </Text>
          )}
        </div>
        <Text
          className={cn(
            "my-4 text-lg",
            isPopular ? "text-gray-100" : "text-gray-600"
          )}
        >
          {description}
        </Text>
        <div className="flex flex-col gap-3 mt-6">
          <Title as="h3" className={cn("text-lg", isPopular && "text-white")}>
            Unlimited Website Creation
          </Title>
          <Button
            onClick={onGetStarted}
            rounded="lg"
            className={cn(
              "mt-auto my-8 w-full font-bold rounded-full max-w-fit transition-transform duration-300 hover:scale-110",
              isPopular
                ? "bg-white text-black hover:bg-gray-100"
                : "bg-black text-white hover:bg-gray-900"
            )}
            size="lg"
            variant={isPopular ? "flat" : "solid"}
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

interface PricingPlansProps {
  onPlanSelect?: (plan: IPackage) => void;
}

const PricingPlans: React.FC<PricingPlansProps> = ({ onPlanSelect }) => {
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(packageCodeSchema),
  });
  const onSubmit = async (data: FormValues) => {
    try {
      const res = await getPackageByCode(data.code);
      const packageItem = res?.data;
      if (packageItem) {
        onPlanSelect?.(packageItem);
      }
    } catch (e: any) {
      setError("code", {
        type: "manual",
        message: e?.message || "Something went wrong",
      });
    }
  };

  const { request: getActivePackages, loading } = useApi({
    request: PackageRepository.getPackageActive,
  });

  const { request: getPackageByCode, loading: loadingPackageByCode } = useApi({
    request: PackageRepository.getPackageByCode,
  });

  const [plans, setPlans] = useState<IPackage[]>([]);

  useEffect(() => {
    getActivePackages().then((res) => {
      setPlans(res?.data?.items || []);
    });
  }, []);

  return (
    <div className="container max-w-screen-xl w-full mx-auto px-4">
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => {
          const monthlyPrice = calculateMonthlyPrice(
            plan?.price,
            plan?.duration
          );
          return (
            <PricingPlan
              key={plan?.id}
              name={plan?.name}
              price={plan?.price}
              monthlyPrice={monthlyPrice}
              duration={plan?.duration}
              description={plan?.description || ""}
              onGetStarted={() => onPlanSelect?.(plan)}
              isPopular={!!plan?.isPopular}
            />
          );
        })}
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-8">
          <div className="relative w-full max-w-[32%]">
            <Input
              label="Package Code"
              type="text"
              size="lg"
              placeholder="Enter Code"
              autoComplete="off"
              className="[&>label>span]:font-medium col-span-full"
              {...register("code")}
              error={errors.code?.message as string}
            />
          </div>

          <Button
            className="mt-2 mb-5 w-full font-bold max-w-fit"
            type="submit"
            id="form-settings"
            size="lg"
            disabled={watch("code") === "" || loadingPackageByCode}
          >
            Get Started
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PricingPlans;